/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { SortableFields } from 'embercom/lib/suggestions/constants';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import Service, { inject as service } from '@ember/service';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { tracked } from '@glimmer/tracking';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { EntityType } from 'embercom/models/data/entity-types';
import { REVIEWABLE_CONTENT_ENTITIES } from 'embercom/lib/ai-content-library/constants';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { getOwner } from '@ember/application';
import type IntlService from 'embercom/services/intl';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { fetchTaskSuggestions } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/task-suggestions/task-suggestions-data-resources';
import type Store from '@ember-data/store';
import type ReportingChartService from './reporting-chart-service';
import { applyFilters } from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/optimize-suggestion-filter-helper';
import moment from 'moment-timezone';
import type ContentFragment from 'embercom/models/content-service/content-fragment';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import { getDuplicateReviewBaseReason } from 'embercom/components/knowledge-hub/content-editor/shared/utils/side-by-side-review-helper';
import { ContentAction } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import {
  type OptimizeSuggestionFilterState,
  type OptimizeSuggestionItem,
  OptimizeSuggestionSortBy,
  OptimizeSuggestionType,
} from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/constants';
import ajax from 'embercom/lib/ajax';

export default class OptimizeSuggestionsService extends Service {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare reportingChartService: ReportingChartService;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked fullSuggestions: OptimizeSuggestionItem[] = [];
  @tracked filterState: OptimizeSuggestionFilterState = {};
  @tracked sortBy: OptimizeSuggestionSortBy = OptimizeSuggestionSortBy.Impact;
  @tracked isEmptyState = false;

  constructor() {
    super(...arguments);
    this.setFilterState({ sortBy: OptimizeSuggestionSortBy.Impact });
  }

  @restartableTask
  *loadSuggestions(): TaskGenerator<void> {
    let promises: Promise<any>[] = [];
    promises.push(this.contentAPI());
    promises.push(this.loadTaskSuggestions());

    let shouldFetchImpact = this.appService.app.canUseFeature(
      'ai-insights-updated-impact-ranking-for-suggestions',
    );

    if (shouldFetchImpact) {
      promises.push(this.fetchSuggestionImpact());
    }

    let results = yield Promise.all(promises);
    let api = results[0];
    let taskSuggestionsResponse = results[1];
    let impactResponse = shouldFetchImpact ? results[2] : null;

    let contentSuggestions: OptimizeSuggestionItem[] = [];
    let taskSuggestions: OptimizeSuggestionItem[] = [];

    if (!api.isLoadingPage) {
      contentSuggestions = yield this.compileContentSuggestions(api.contentWrappers);
    }

    if (taskSuggestionsResponse.length > 0) {
      taskSuggestions = this.compileTaskSuggestions(taskSuggestionsResponse);
    }

    let allSuggestions = [...contentSuggestions, ...taskSuggestions];

    if (shouldFetchImpact && impactResponse?.impact_map) {
      allSuggestions = this.applyImpactRankings(allSuggestions, impactResponse.impact_map);
    }

    this.fullSuggestions = allSuggestions;
    this.isEmptyState = this.fullSuggestions.length === 0;
  }

  get suggestions(): OptimizeSuggestionItem[] {
    let currentSortBy = this.sortBy;

    if (!this.fullSuggestions || !this.filterState) {
      return [];
    }

    let filtered = applyFilters(this.appService, this.fullSuggestions, {
      ...this.filterState,
      sortBy: currentSortBy,
    });

    return this.applySorting(filtered, currentSortBy);
  }

  get isNoSuggestions(): boolean {
    return this.suggestions.length === 0;
  }

  setFilterState(filterState: OptimizeSuggestionFilterState) {
    this.filterState = filterState;
  }

  setSortBy(sortBy: OptimizeSuggestionSortBy) {
    this.sortBy = sortBy;
  }

  private applySorting(
    suggestions: OptimizeSuggestionItem[],
    sortBy: OptimizeSuggestionSortBy,
  ): OptimizeSuggestionItem[] {
    return [...suggestions].sort((a, b) => {
      if (sortBy === OptimizeSuggestionSortBy.Impact) {
        return this.impactComparator(a, b);
      } else {
        return this.dateComparator(a, b);
      }
    });
  }

  /* sort by impact with date tiebreaker */
  private impactComparator(a: OptimizeSuggestionItem, b: OptimizeSuggestionItem): number {
    if (a.impactRanking !== b.impactRanking) {
      return b.impactRanking - a.impactRanking;
    }

    return this.dateComparator(a, b);
  }

  private dateComparator(a: OptimizeSuggestionItem, b: OptimizeSuggestionItem): number {
    // Handle null/undefined createdAt values - sort them to the end
    if (!a.createdAt && !b.createdAt) {
      return 0;
    }
    if (!a.createdAt) {
      return 1;
    }
    if (!b.createdAt) {
      return -1;
    }
    return b.createdAt.localeCompare(a.createdAt);
  }

  findSuggestionByEntityId(entityId: string) {
    return this.fullSuggestions.find((suggestion) => suggestion.entityId === entityId);
  }

  removeSuggestionFromList(entityId: string) {
    this.fullSuggestions = this.fullSuggestions.filter(
      (suggestion) => suggestion.entityId !== entityId,
    );
  }

  getNextSuggestion(currentEntityId: string): OptimizeSuggestionItem | undefined {
    let currentIndex = this.suggestions.findIndex((s) => s.entityId === currentEntityId);

    if (currentIndex === -1) {
      return this.suggestions[0]; // Default to first if not found
    }

    // Try next suggestion
    if (currentIndex < this.suggestions.length - 1) {
      return this.suggestions[currentIndex + 1];
    }

    // Try previous if at end
    if (currentIndex > 0) {
      return this.suggestions[currentIndex - 1];
    }

    return undefined; // No more suggestions
  }

  predictedSuggestionsCount(filterState: OptimizeSuggestionFilterState): number {
    return applyFilters(this.appService, this.fullSuggestions, {
      ...filterState,
      sortBy: this.sortBy,
    }).length;
  }

  getSuggestionGenerationDates(): Array<{
    timestamp: string | undefined;
    formattedTimestamp: string;
    label: string;
  }> {
    let timestamps = this.fullSuggestions.map((suggestion) => suggestion.createdAt);

    if (timestamps.length === 0) {
      return [];
    }

    // Group by unique date (day only, ignoring time) and keep one timestamp per date
    let dateMap = new Map<string, string>();
    timestamps.forEach((timestamp) => {
      let momentDate = moment.tz(timestamp, this.appService.app.timezone);
      let dateKey = momentDate.format('YYYY-MM-DD');

      // Keep the first timestamp we encounter for each date
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, timestamp);
      }
    });

    // Convert map to array and format labels
    return Array.from(dateMap.values())
      .sort((a, b) => {
        return (
          moment.tz(b, this.appService.app.timezone).valueOf() -
          moment.tz(a, this.appService.app.timezone).valueOf()
        );
      })
      .map((timestamp) => {
        let startOfWeek = moment
          .tz(timestamp, this.appService.app.timezone)
          .startOf('week')
          .startOf('day');
        let endOfWeek = moment
          .tz(timestamp, this.appService.app.timezone)
          .endOf('week')
          .endOf('day');

        return {
          timestamp,
          formattedTimestamp: moment
            .tz(timestamp, this.appService.app.timezone)
            .format('YYYY-MM-DD'),
          label: `${startOfWeek.format('MMM D, YYYY')} - ${endOfWeek.format('MMM D, YYYY')}`,
        };
      });
  }

  getRelevantTopics(): string[] {
    return this.fullSuggestions
      .map((suggestion) => suggestion.topicList)
      .flat()
      .uniq()
      .compact()
      .sort();
  }

  private async contentAPI() {
    let sort_by = SortableFields.Priority;

    let api = new AiContentLibraryApi(getOwner(this), {
      object_types: this.reviewableContentEntities(),
      content_wrapper_types: [ContentWrapperType.AI_CONTENT],
      app_id: this.appService.app.id as string,
      per_page: 100,
      content_review_statuses: [Status.Pending],
      sort_by,
      sort_direction: 'desc',
    });

    let promises: Promise<any>[] = [api.loadPage()];
    promises.push(this.knowledgeHubService.fetchAvailableLocalesFromMessengerAndHelpCenter());
    await Promise.all(promises);

    this.contentSuggestionsService.listApi = api;

    return api;
  }

  private async loadTaskSuggestions() {
    let taskSuggestions = await fetchTaskSuggestions(
      this.store,
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
    );
    return taskSuggestions;
  }

  private async fetchSuggestionImpact() {
    try {
      let response = await ajax({
        url: '/ember/ai_insights/suggestion_impact',
        type: 'GET',
        data: {
          app_id: this.appService.app.id,
        },
      });
      return response;
    } catch (error) {
      console.error('Error fetching suggestion impact:', error);
      return null;
    }
  }

  private applyImpactRankings(
    suggestions: OptimizeSuggestionItem[],
    impactMap: Record<string, string>,
  ): OptimizeSuggestionItem[] {
    let impactToRanking: Record<string, number> = {
      low: 0,
      medium: 1,
      high: 2,
    };

    return suggestions.map((suggestion) => {
      let prefix =
        suggestion.suggestionType === OptimizeSuggestionType.ContentGap
          ? 'content_suggestion'
          : 'task_suggestion';
      let impactKey = `${prefix}_${suggestion.entityId}`;
      let impactValue = impactMap[impactKey];

      if (impactValue && impactToRanking[impactValue] !== undefined) {
        return {
          ...suggestion,
          impactRanking: impactToRanking[impactValue],
        };
      }

      return suggestion;
    });
  }

  private compileTaskSuggestions(taskSuggestions: TaskSuggestion[]) {
    return taskSuggestions.map((taskSuggestion: TaskSuggestion) => {
      let id = taskSuggestion.id || Math.floor(Math.random() * 1000000).toString();
      let createdAt: string;
      if (taskSuggestion.createdAt) {
        if (typeof taskSuggestion.createdAt === 'string') {
          createdAt = taskSuggestion.createdAt;
        } else {
          createdAt = taskSuggestion.createdAt.toISOString();
        }
      } else {
        createdAt = new Date().toISOString();
      }

      let suggestionType = this.taskSuggestionType(taskSuggestion);
      let icon = '' as InterfaceIconName;
      let suggestionTypeDisplay = '';

      if (suggestionType === OptimizeSuggestionType.DataGap) {
        icon = 'recipient-data' as InterfaceIconName;
        suggestionTypeDisplay = this.intl.t(
          `components.reporting.automation.optimize-suggestions.filter.reason.data-gap`,
        );
      } else if (suggestionType === OptimizeSuggestionType.ActionGap) {
        icon = 'webhook' as InterfaceIconName;
        suggestionTypeDisplay = this.intl.t(
          `components.reporting.automation.optimize-suggestions.filter.reason.action-gap`,
        );
      }

      return {
        entityId: id,
        contentType: undefined,
        suggestionType,
        suggestionTypeDisplay,
        title: taskSuggestion.actionName,
        description: taskSuggestion.actionSummary,
        createdAt,
        conversationIds: taskSuggestion.sampledConversationIds,
        conversationCount: taskSuggestion.sampledConversationIds.length,
        suggestionState: taskSuggestion.status,
        topicList: taskSuggestion.topicNameList,
        subtopicList: taskSuggestion.subtopics,
        impactRanking: taskSuggestion.projectedConversationsRanking ?? 1, // fallback to Medium if null
        suggestion: taskSuggestion,
        icon,
      };
    });
  }

  private async compileContentSuggestions(contentWrappers: ContentWrapper[]) {
    return await Promise.all(
      contentWrappers.map(async (contentWrapper: ContentWrapper) => {
        let content = contentWrapper.contents.firstObject;
        if (!content || !contentWrapper.entityId) {
          return null;
        }

        let suggestionTypeDisplay = this.intl.t(
          `components.reporting.automation.optimize-suggestions.filter.reason.content-gap`,
        );

        return {
          entityId: contentWrapper.entityId.toString(),
          contentType: content.contentType,
          suggestionType: 'content-gap',
          suggestionTypeDisplay,
          title: this.contentTitle(content.contentReviewType, content.contentType),
          description: await this.contentReviewDescription(contentWrapper, content),
          // Use when the AI suggestion was created (most_recent_content_review_request_created_at),
          // not when the underlying content was created (content.createdAt).
          // Fallbacks prevent null crashes during client-side sorting.
          createdAt:
            content.contentData.most_recent_content_review_request_created_at ||
            content.createdAt?.toISOString() ||
            new Date().toISOString(),
          conversationIds: contentWrapper.contentData.suggestion_metadata.included_conversations,
          conversationCount:
            contentWrapper.contentData.suggestion_metadata.included_conversations?.length ?? 0,
          suggestionState: content.contentData.status,
          topicList: [content.contentData.suggestion_metadata.topic],
          subtopicList: content.contentData.suggestion_metadata.subtopics,
          impactRanking: content.contentData.projected_conversations_ranking ?? 0, // fallback to Low if null
          suggestion: contentWrapper,
          icon: 'knowledge',
        };
      }),
    );
  }

  private taskSuggestionType(taskSuggestion: TaskSuggestion) {
    return taskSuggestion.isDataGap
      ? OptimizeSuggestionType.DataGap
      : OptimizeSuggestionType.ActionGap;
  }

  private contentTitle(contentReviewType: ReviewType, contentType: EntityType) {
    let objectKey = contentType === EntityType.ContentSnippet ? 'snippet' : 'article';
    switch (contentReviewType) {
      case ReviewType.CREATE_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.add-content');
      case ReviewType.EDIT_CONTENT:
        //next 2 can also be snippet but i need to figure out how to handle that
        return this.intl.t(`ai-agent.suggestions.list-item.titles.edit-${objectKey}`);
      case ReviewType.DELETE_CONTENT:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.delete-${objectKey}`);
      case ReviewType.DUPLICATE_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.duplicate-content');
      case ReviewType.CONTRADICTING_CONTENT:
        return this.intl.t('ai-agent.suggestions.list-item.titles.contradicting-content');
      case ReviewType.UNDERPERFORMING_CONTENT:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.underperforming-${objectKey}`);
      default:
        return this.intl.t(`ai-agent.suggestions.list-item.titles.fallback-${objectKey}`);
    }
  }

  private contentReviewDescription(contentWrapper: ContentWrapper, content: ContentFragment) {
    if (content.contentReviewType === ReviewType.UNDERPERFORMING_CONTENT) {
      return this.intl.t('ai-agent.suggestions.list-item.underperforming-content-reason', {
        contentTitle: contentWrapper.title,
      });
    }
    if (contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation) {
      return contentWrapper.contentReviewSuggestionMetadata.seed_content_explanation;
    }

    if (content.contentData.latest_content_review_request_reason) {
      return content.contentData.latest_content_review_request_reason;
    }

    if (content.contentReviewType === ReviewType.DUPLICATE_CONTENT) {
      return (
        this.loadDuplicateReason(contentWrapper) ||
        this.intl.t('ai-agent.suggestions.list-item.review-reason-fallback')
      );
    }

    return this.intl.t('ai-agent.suggestions.list-item.review-reason-fallback');
  }

  private async loadDuplicateReason(contentWrapper: ContentWrapper) {
    let relationshipId = contentWrapper.entityId!;

    try {
      let relationship = (await this.store.findRecord(
        'content-service/content-section-relationship',
        relationshipId,
      )) as ContentSectionRelationship;

      await Promise.all([
        relationship.sourceSection.loadRelatedContent(ContentAction.EDIT),
        relationship.targetSection.loadRelatedContent(ContentAction.EDIT),
      ]);

      // Load the base review reason for duplicate content
      return getDuplicateReviewBaseReason(
        relationship.sourceSection,
        relationship.targetSection,
        this.intl,
      );
    } catch (e) {
      console.warn('Failed to load con/dupe reason data for relationship', relationshipId, e);

      return null;
    }
  }

  private reviewableContentEntities(): EntityType[] {
    let reviewableContentEntities = [...REVIEWABLE_CONTENT_ENTITIES];
    reviewableContentEntities.push(EntityType.ArticleContent);
    reviewableContentEntities.push(EntityType.ContentSectionRelationship);
    return reviewableContentEntities;
  }
}

declare module '@ember/service' {
  interface Registry {
    optimizeSuggestionsService: OptimizeSuggestionsService;
    'optimize-suggestions-service': OptimizeSuggestionsService;
  }
}
